<template>
  <div class="whiteboard-guest">
    <!-- Dummy-proof reconnect fallback: shown only if auto-recovery can't restore
         the link in time. Reloading re-joins the same persistent room. -->
    <div v-if="showReloadPrompt" class="reconnect-banner">
      <span
        ><i class="fas fa-triangle-exclamation"></i> Bağlantı yeniden
        kurulamadı.</span
      >
      <button class="reconnect-reload-btn" @click="reloadPage">
        <i class="fas fa-rotate-right"></i> Sayfayı Yenile
      </button>
    </div>
    <!-- Loading Overlay (shows after gesture, until WebRTC connects) -->
    <transition name="fade-loading">
      <div v-if="gestured && !guestReady" class="loading-overlay">
        <div class="loading-card">
          <!-- P4-16 — invalid / expired invite link -->
          <template v-if="accessDenied">
            <div class="loading-spinner">
              <i class="fas fa-exclamation-triangle spinner-icon access-denied-icon"></i>
            </div>
            <h2 class="loading-title">Bağlantı Geçersiz</h2>
            <p class="loading-subtitle">
              Bu oturum bağlantısı geçersiz veya süresi dolmuş. Lütfen
              öğretmeninizden yeni bir bağlantı isteyin.
            </p>
          </template>
          <template v-else>
            <div class="loading-spinner">
              <div class="spinner-ring"></div>
              <i class="fas fa-user-friends spinner-icon"></i>
            </div>
            <h2 class="loading-title">Odaya Bağlanılıyor</h2>
            <p class="loading-subtitle">Oda sahibine bağlanılıyor, lütfen bekleyin...</p>
            <div class="loading-steps">
              <div class="loading-step" :class="{ done: connected }">
                <i class="fas" :class="connected ? 'fa-check-circle' : 'fa-circle-notch fa-spin'"></i>
                <span>Sunucuya bağlanılıyor</span>
              </div>
              <div class="loading-step" :class="{ done: roomJoined }">
                <i class="fas" :class="roomJoined ? 'fa-check-circle' : (connected ? 'fa-circle-notch fa-spin' : 'fa-circle')"></i>
                <span>Odaya katılınıyor</span>
              </div>
              <div class="loading-step" :class="{ done: webrtcConnected }">
                <i class="fas" :class="webrtcConnected ? 'fa-check-circle' : (roomJoined ? 'fa-circle-notch fa-spin' : 'fa-circle')"></i>
                <span>Oda sahibiyle bağlantı kuruluyor</span>
              </div>
            </div>
            <!-- P4-16 — slow/unstable connection warning + manual retry -->
            <div v-if="connectionUnstable" class="loading-unstable">
              <p class="unstable-text">
                <i class="fas fa-wifi"></i>
                İnternet bağlantınız stabil değil, bağlantı yeniden deneniyor…
              </p>
              <button class="retry-btn" @click="retryConnection">Yeniden Dene</button>
            </div>
          </template>
        </div>
      </div>
    </transition>
    <!-- Left Sidebar (Tools Only - Limited by Host Permissions) -->
    <div
      class="left-sidebar"
      :class="{
        collapsed: !sidebarVisible,
        'mobile-open': leftSidebarOpen,
        'fullscreen-hidden': isFullscreen,
      }"
      @click.self="leftSidebarOpen && closeMobileSidebars()"
    >
      <!-- Mobile Sidebar Toggle (Only visible on mobile) -->
      <div class="mobile-sidebar-toggle mobile-only" @click="toggleLeftSidebar">
        <i class="fas" :class="leftSidebarOpen ? 'fa-times' : 'fa-bars'"></i>
        <span>{{ leftSidebarOpen ? "Kapat" : "Araçlar" }}</span>
      </div>

      <!-- Sidebar Content -->
      <div class="sidebar-content">
        <!-- Compact Toolbox (Guest tools based on permissions) -->
        <div class="sidebar-section toolbox-section">
          <Toolbox
            :activeTool="activeTool"
            :isGuest="true"
            :permissions="guestPermissions"
            :canUndo="false"
            :canRedo="false"
            @tool-changed="handleToolChange"
            @tool-options-changed="handleToolOptionsChange"
            @clear-canvas="handleClearCanvas"
            @undo-action="handleUndo"
            @redo-action="handleRedo"
          />
        </div>
      </div>
    </div>

    <!-- Main Canvas Area -->
    <div class="main-content">
      <CanvasStage
        ref="canvasStage"
        :roomId="roomId"
        :activeTool="activeTool"
        :toolOptions="toolOptions"
        :sidebarVisible="sidebarVisible"
        :isGuest="true"
        :permissions="guestPermissions"
        @canvas-updated="handleCanvasUpdate"
        @toggle-sidebars="toggleSidebars"
        @toggle-fullscreen="toggleFullscreen"
      />

      <!-- Bottom Floating Controls -->
      <div class="bottom-controls" :class="{ 'fullscreen-hidden': isFullscreen }">
        <!-- Local Camera (Bottom Left) - Guest's own camera -->
        <div class="bottom-left-controls" :class="{ maximize: !gestured }">
          <div
            class="floating-camera local-cam"
            :class="{ hidden: !localCameraVisible }"
            :style="{ display: localCameraVisible ? 'block' : 'none' }"
          >
            <div v-if="!gestured" class="camera-header">
              <div class="permission-status">
                <div v-if="!hasAudioDevice && !hasVideoDevice" class="no-devices">
                  Kamera veya mikrofon bulunamadı
                </div>
                <div v-else class="device-status">
                  <div v-if="hasAudioDevice" class="audio-status">
                    <i
                      class="fas fa-microphone"
                      :class="{
                        'text-success': audioPermissionGranted,
                        'text-danger': audioPermissionDenied,
                        'text-muted': !audioPermissionGranted && !audioPermissionDenied,
                      }"
                    ></i>
                    <span v-if="audioPermissionDenied">Mikrofon reddedildi</span>
                    <span v-else-if="audioPermissionGranted">Mikrofon hazır</span>
                    <span v-else>Mikrofon mevcut</span>
                  </div>
                  <div v-if="hasVideoDevice" class="video-status">
                    <i
                      class="fas fa-video"
                      :class="{
                        'text-success': videoPermissionGranted,
                        'text-danger': videoPermissionDenied,
                        'text-muted': !videoPermissionGranted && !videoPermissionDenied,
                      }"
                    ></i>
                    <span v-if="videoPermissionDenied">Kamera reddedildi</span>
                    <span v-else-if="videoPermissionGranted">Kamera hazır</span>
                    <span v-else>Kamera mevcut</span>
                  </div>
                </div>
              </div>
              <button
                @click="
                  audioPermissionGranted || videoPermissionGranted
                    ? handleGesture(true)
                    : handleGesture(false)
                "
                class="btn"
                :class="{
                  'btn-primary': audioPermissionGranted || videoPermissionGranted,
                  'btn-secondary': !audioPermissionGranted && !videoPermissionGranted,
                }"
                :disabled="!hasAudioDevice && !hasVideoDevice"
              >
                {{
                  audioPermissionGranted || videoPermissionGranted
                    ? "Hazır Olduğunda Buraya Tıkla"
                    : "İzinleri Al"
                }}
              </button>
            </div>
            <div v-else class="camera-header">
              <span>Senin Kameran</span>
              <button class="hide-btn" @click="localCameraVisible = false">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <LocalCamera
              ref="localCamera"
              @mic-toggle="handleMicToggle"
              @camera-toggle="handleCameraToggle"
            >
              <template #video>
                <video
                  style="height: 100%; width: 100%"
                  autoplay
                  playsinline
                  :muted="gestured || micMuted"
                  ref="vp"
                />
              </template>
            </LocalCamera>
          </div>

          <!-- Local Camera Toggle Button -->
          <button
            class="camera-toggle-btn local"
            @click="localCameraVisible = !localCameraVisible"
            v-if="!localCameraVisible"
          >
            <i class="fas fa-video"></i>
          </button>
        </div>

        <!-- Host Camera (Bottom Right) -->
        <div class="bottom-right-controls">
          <div
            class="floating-camera host-cam"
            :class="{ hidden: !hostCameraVisible }"
            :style="{ display: hostCameraVisible ? 'block' : 'none' }"
          >
            <div class="camera-header">
              <span>Oda Sahibi Kamerası</span>
              <button class="hide-btn" @click="hostCameraVisible = false">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <HostCamera :hostOnline="hostConnected" :hostName="hostName">
              <template #video>
                <video
                  style="height: 100%; width: 100%"
                  autoplay
                  playsinline
                  :muted="remoteMuted"
                  ref="vpremote"
                />
                <div v-if="remoteMuted" class="mic-off-indicator">
                  <i class="fas fa-microphone-slash mic-off-icon"></i>
                  <span class="mic-off-text">Mikrofon Kapalı</span>
                </div>
              </template>
            </HostCamera>
          </div>

          <!-- Host Camera Toggle Button -->
          <button
            class="camera-toggle-btn host"
            @click="hostCameraVisible = !hostCameraVisible"
            v-if="!hostCameraVisible"
          >
            <i class="fas fa-user-tie"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Right Sidebar (Chat Only - No Permission Controls) -->
    <div
      class="right-sidebar"
      :class="{
        collapsed: !sidebarVisible,
        'mobile-open': rightSidebarOpen,
        'fullscreen-hidden': isFullscreen,
      }"
      @click.self="rightSidebarOpen && closeMobileSidebars()"
    >
      <!-- Mobile Sidebar Toggle (Only visible on mobile) -->
      <div class="mobile-sidebar-toggle mobile-only" @click="toggleRightSidebar">
        <i class="fas" :class="rightSidebarOpen ? 'fa-times' : 'fa-bars'"></i>
        <span>{{ rightSidebarOpen ? "Close" : "Chat" }}</span>
      </div>

      <!-- Sidebar Content -->
      <div class="sidebar-content">
        <b-tabs class="h-100 d-flex flex-column" fill>
          <b-tab title="Genel" active class="genel-tab-pane">
            <!-- Guest Status Info -->
            <div class="sidebar-section guest-status compact">
              <div class="status-info">
                <h4 class="status-title">Misafir Modu</h4>
                <div class="connection-info">
                  <span class="connection-status online">
                    <i class="fas fa-circle"></i>
                    Oda Sahibi'ne Bağlı
                  </span>
                </div>
                <div class="permissions-summary">
                  <h5>Available Tools:</h5>
                  <div class="permission-list">
                    <span v-if="guestPermissions.canDraw" class="permission-item active">
                      <i class="fas fa-pen"></i> Çizim
                    </span>
                    <span v-if="guestPermissions.canErase" class="permission-item active">
                      <i class="fas fa-eraser"></i> Silme
                    </span>
                    <span v-if="guestPermissions.canAddShapes" class="permission-item active">
                      <i class="fas fa-shapes"></i> Şekiller
                    </span>
                    <span
                      v-if="guestPermissions.canUploadBackground"
                      class="permission-item active"
                    >
                      <i class="fas fa-image"></i> Yükle
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Automatic WebRTC Connection Status -->
            <div class="sidebar-section connection-status">
              <div class="connection-container">
                <div class="connection-header">
                  <h5>Doğrudan Bağlantı Durumu</h5>
                </div>
                <div class="connection-info">
                  <div class="connection-status" :class="{ connected: webrtcConnected }">
                    <i class="fas fa-circle"></i>
                    {{
                      webrtcConnected ? "Oda sahibi'ne Bağlı" : "Oda sahibi'ne Bağlanıyor..."
                    }}
                  </div>
                  <!-- <small v-if="!webrtcConnected" style="color: #666; font-size: 11px">
                    Bağlantı, misafir katıldığında otomatik olarak kurulacaktır
                  </small> -->
                </div>
              </div>
            </div>
          </b-tab>
          <b-tab title="Mesajlar" class="chat-tab-pane">
            <!-- Chat Box -->
            <div class="sidebar-section chat-box border-0 p-0">
              <div class="chat-container">
                <div class="chat-header">
                  <h4>Chat</h4>
                  <span class="connection-status" :class="{ connected: connected }">
                    <i class="fas fa-circle"></i>
                    {{ connected ? "Bağlandı" : "Bağlantı Kesildi" }}
                  </span>
                </div>
                <div class="chat-messages" ref="chatMessages">
                  <div
                    v-for="message in messages"
                    :key="message.id"
                    class="message"
                    :class="{
                      system: message.from === 'system',
                      own: message.from === 'peer',
                    }"
                  >
                    <div class="message-content">{{ message.content }}</div>
                    <div class="message-time">{{ formatTime(message.timestamp) }}</div>
                  </div>
                </div>
                <div class="chat-input" v-if="connected || webrtcConnected">
                  <input
                    v-model="newMessage"
                    @keyup.enter="sendMessage"
                    placeholder="Mesaj yaz..."
                    class="message-input"
                  />
                  <button @click="sendMessage" class="send-btn">
                    <i class="fas fa-paper-plane"></i>
                  </button>
                </div>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BTabs, BTab } from "bootstrap-vue";
import Toolbox from "./components/Toolbox.vue";
import LocalCamera from "./components/LocalCamera.vue";
import CanvasStage from "./components/CanvasStage.vue";
import HostCamera from "./components/HostCamera.vue";
import io from "socket.io-client";
import WebRTCService from "./services/WebRTCService.js";
import axios from "@axios";
import useCamera from "./mixins/useCamera.js";

export default {
  name: "GuestView",
  mixins: [useCamera],
  components: {
    Toolbox,
    LocalCamera,
    CanvasStage,
    HostCamera,
    BButton,
    BTabs,
    BTab,
  },
  data() {
    return {
      gestured: false,
      // Sidebar state
      sidebarVisible: true,
      leftSidebarOpen: false,
      rightSidebarOpen: false,
      isFullscreen: false,

      // Floating camera state
      localCameraVisible: true,
      hostCameraVisible: true,

      // Socket connection (only for chat and basic room management)
      socket: null,
      connected: false,
      roomId: null,

      // WebRTC for direct P2P communication
      webrtc: null,
      webrtcConnected: false,
      hostConnected: false,
      hostName: "Uzman",
      // Loading state
      guestReady: false,
      roomJoined: false,
      // P4-16 — connection health surfaced to the loading overlay.
      connectionUnstable: false,
      showReloadPrompt: false,
      accessDenied: false,

      // Messages
      messages: [],
      newMessage: "",

      // Guest permissions received from host
      guestPermissions: {
        canDraw: true,
        canErase: true,
        canAddShapes: true,
        canUploadBackground: false, // Guests cannot upload backgrounds
        canClear: false, // Guests permanently cannot clear canvas
        canSelect: false, // Guests permanently cannot use select tool
        canDragCanvas: false, // Guests cannot drag the canvas
        canUndo: false, // Guests permanently cannot undo
        canRedo: false, // Guests permanently cannot redo
      },

      activeTool: "pen",
      toolOptions: {
        select: {},
        pen: {
          strokeWidth: 10,
          stroke: "#000000",
        },
        eraser: {
          strokeWidth: 10,
        },
        rectangle: {
          strokeWidth: 10,
          stroke: "#000000",
          fill: "transparent",
          filled: false,
        },
        circle: {
          strokeWidth: 10,
          stroke: "#000000",
          fill: "transparent",
          filled: false,
        },
      },

    };
  },

  methods: {
    // Modified handleGesture method
    async handleGesture(ready = false) {
      try {
        if (
          !this.videoPermissionGranted &&
          !this.audioPermissionGranted &&
          (this.hasAudioDevice || this.hasVideoDevice)
        ) {
          // Check available devices first

          // Request permissions separately
          /*  const audioGranted = */ await this.requestAudioPermission();
          /*  const videoGranted =  */ await this.requestVideoPermission();

          // Create combined stream for WebRTC
          const combined = this.combineStreams();

          if (combined) {
            this.localStream = combined;
          }
        }

        if (ready && this.localStream) {
          this.gestured = true;
          this.initializeSocket();
          // P4-16 — surface an "unstable internet" message if we don't fully
          // connect in time, instead of an indefinite spinner.
          this.startConnectWatchdog();
        }
      } catch (err) {
        console.error("Error in handleGesture:", err);
      }
    },

    // Socket connection methods
    async initializeSocket() {
      try {
        // Get room ID from route params
        this.roomId = this.$route.params.roomid;

        if (!this.roomId) {
          console.error("No room ID provided");
          return;
        }

        // Connect to socket server
        // P2-9 — the guest is anonymous; it authenticates with the room-scoped
        // invite token the host embedded in the URL (?t=…).
        this.socket = io(axios.defaults.baseURL.replace("/api", ""), {
          transports: ["websocket", "polling"],
          // Function form so a reconnect always re-reads the current invite token.
          auth: (cb) => cb({ token: this.$route.query.t }),
          // Keep retrying forever with a bounded backoff so a flaky mobile link
          // recovers on its own instead of stranding the guest on one failed try.
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
        });

        this.socket.on("connect", () => {
          console.log("Guest connected to socket server");
          this.connected = true;
          this.joinRoom();
          // Note: WebRTC will be initialized when connection data is provided
        });

        this.socket.on("connected", (data) => {
          console.log("Server confirmed connection:", data);
        });

        this.socket.on("peer-info", (roomid, who, peerConnection) => {
          console.log(`Peer info: ${who} in room ${roomid}`);
          // WebRTC signaling payloads (offer/answer/ICE candidates) are handled by
          // WebRTCService's own peer-info listener. Ignore them here so trickled
          // host ICE candidates don't repeatedly re-fire "host joined" (P0-1 fix).
          if (
            peerConnection &&
            typeof peerConnection.type === "string" &&
            peerConnection.type.startsWith("webrtc-")
          ) {
            return;
          }
          if (who === "host") {
            this.handleHostJoined();
            // WebRTC is already initialized when guest joins room
          }
        });

        // Presence: the host's socket dropped. Show "reconnecting" right away
        // (faster than the WebRTC ICE timeout). Cleared on WebRTC 'connected'.
        this.socket.on("peer-left", (roomid, who) => {
          if (who === "host") {
            console.log("👋 Host disconnected (socket) — reconnecting");
            this.hostConnected = false;
            this.webrtcConnected = false;
            this.connectionUnstable = true;
            this.armReloadPrompt();
          }
        });

        // A same-device host takeover replaces only the host's authenticated
        // transport. The room, board, chat and guest socket remain intact while
        // the host signs back in and reconnects.
        this.socket.on("host-session-replaced", () => {
          this.hostConnected = false;
          this.webrtcConnected = false;
          this.connectionUnstable = true;
          this.messages.push({
            id: `host-replaced-${Date.now()}`,
            from: "system",
            content: "Oda sahibi yeniden bağlanıyor…",
            timestamp: new Date(),
          });
          this.armReloadPrompt();
        });

        // P1-5 — apply the host's live whiteboard ops relayed by the server,
        // and the current snapshot on (re)join / late-join.
        this.socket.on("canvas-op", (roomid, who, data) => {
          if (this.$refs.canvasStage) {
            this.$refs.canvasStage.applyCanvasUpdate(data);
          }
        });
        this.socket.on("canvas-state", (roomid, state) => {
          if (this.$refs.canvasStage) {
            this.$refs.canvasStage.applyCanvasUpdate(state);
          }
        });

        // P1-8 — render persisted chat history on join / reconnect, de-duplicated
        // by id so a message that also arrives live isn't shown twice.
        this.socket.on("chat-history", (roomid, history) => {
          const existing = new Set(this.messages.map((m) => m.id));
          (history || []).forEach((msg) => {
            const id = `${msg.ts}-${msg.who}`;
            if (existing.has(id)) return;
            existing.add(id);
            this.messages.push({
              id,
              from: msg.who === "peer" ? "peer" : "host",
              content: msg.text,
              timestamp: new Date(msg.ts),
            });
          });
          this.messages.sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );
          this.scrollChatToBottom();
        });

        // P1-6 — server-relayed chat. Render every message from the socket so
        // there is a single source of truth for both peers (our own messages
        // arrive back here echoed by the server).
        this.socket.on("chat-msg", (roomid, msg) => {
          const mine = msg.who === "peer";
          this.messages.push({
            id: `${msg.ts}-${msg.who}`,
            from: mine ? "peer" : "host",
            content: msg.text,
            timestamp: new Date(msg.ts),
          });
          if (!mine) {
            this.showNewMessageToast(msg.text, this.hostName || "Oda Sahibi");
          }
          this.scrollChatToBottom();
        });

        this.socket.on("session-closed", (roomid) => {
          console.log("Session closed by host");
          this.handleSessionClosed();
        });

        this.socket.on("session-terminated", (roomid, redirectUrl) => {
          console.log("🔴 Session terminated via Socket, redirecting to:", redirectUrl);
          this.handleSessionClosed(redirectUrl);
        });

        this.socket.on("disconnect", () => {
          console.log("Disconnected from server");
          this.connected = false;
        });

        // P2-9 — handshake rejected (missing / expired / wrong-room invite token).
        // `connect_error` also fires on transient failures (server restart,
        // network blip) that socket.io auto-recovers from, so only send the guest
        // away when the socket is inactive (a terminal handshake rejection).
        this.socket.on("connect_error", (err) => {
          if (this.socket && this.socket.active) {
            // Transient (server restart / network blip) — socket.io auto-retries.
            // P4-16 — tell the guest instead of leaving a silent frozen spinner.
            this.connectionUnstable = true;
            return;
          }
          console.error("Socket authentication failed:", err.message);
          this.handleAccessDenied();
        });

        // P2-9 — authenticated but not authorized for this specific room.
        this.socket.on("room-denied", () => {
          console.error("Access to this room was denied");
          this.handleAccessDenied();
        });
      } catch (error) {
        console.error("Socket connection failed:", error);
      }
    },

    // P2-9 — the invite link is invalid/expired or not for this room. Tear down
    // and send the guest away rather than leaving them on a dead page.
    handleAccessDenied() {
      if (this._accessDenied) return; // connect_error can fire repeatedly
      this._accessDenied = true;
      this.connected = false;
      // P4-16 — show the reason inside the loading overlay instead of a blocking
      // window.alert(), then redirect after the guest has had a moment to read it.
      this.connectionUnstable = false;
      this.accessDenied = true;
      this.clearConnectWatchdog();
      try {
        if (this.socket) this.socket.disconnect();
        this.cleanupStreams();
      } catch (error) {
        console.log("⚠️ Error during access-denied cleanup:", error);
      }
      setTimeout(() => {
        window.location.href = "https://dikoturkiye.com/";
      }, 5000);
    },

    // P4-16 — resilient TURN credential fetch with exponential backoff. Returns the
    // iceServers array on success, or null after exhausting retries (the caller then
    // falls back to the service's default STUN). Sibling REST calls (e.g. background
    // upload) use the same axios baseURL + `/online` prefix, so the path is correct
    // in prod; the retries absorb the transient 3G failures that caused the
    // intermittent "status code 404 / using default STUN" reports.
    async fetchTurnCredentials(attempts = 3) {
      for (let i = 0; i < attempts; i++) {
        try {
          const { data } = await axios.get("/online/turn-credentials", {
            // Send the room-scoped invite token so the endpoint can authorize this
            // anonymous guest (Concern #3). The host uses its access token header.
            params: { roomId: this.roomId, t: this.$route.query.t },
          });
          if (data && data.iceServers) return data.iceServers;
          return null; // 200 but nothing usable — retrying won't help
        } catch (err) {
          const status = err.response && err.response.status;
          console.warn(
            `TURN credential fetch failed (attempt ${i + 1}/${attempts})`,
            status || err.message
          );
          if (i < attempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** i));
          }
        }
      }
      return null;
    },

    // P4-16 — if the guest hasn't fully connected within the timeout, surface the
    // "unstable internet" state instead of an indefinite spinner.
    startConnectWatchdog() {
      this.clearConnectWatchdog();
      this._connectWatchdog = setTimeout(() => {
        if (!this.guestReady && !this.accessDenied) {
          this.connectionUnstable = true;
        }
      }, 20000);
    },

    clearConnectWatchdog() {
      if (this._connectWatchdog) {
        clearTimeout(this._connectWatchdog);
        this._connectWatchdog = null;
      }
    },

    // P4-16 — user-triggered retry from the unstable-connection overlay: tear down
    // the current socket/WebRTC and re-run the join flow from scratch.
    // Dummy-proof fallback: if the connection stays down past the auto-recovery
    // window, surface an obvious "reload the page" action. A fresh page load
    // rejoins the SAME persistent room and restores the board + media.
    armReloadPrompt() {
      if (this._reloadPromptTimer || this.showReloadPrompt) return;
      this._reloadPromptTimer = setTimeout(() => {
        this._reloadPromptTimer = null;
        this.showReloadPrompt = true;
      }, 20000);
    },
    disarmReloadPrompt() {
      if (this._reloadPromptTimer) {
        clearTimeout(this._reloadPromptTimer);
        this._reloadPromptTimer = null;
      }
      this.showReloadPrompt = false;
    },
    reloadPage() {
      window.location.reload();
    },

    retryConnection() {
      if (this._accessDenied) return;
      // Escalate to relay-preferred on the 2nd+ manual retry (see initializeWebRTC).
      this._retryCount = (this._retryCount || 0) + 1;
      if (this._retryCount >= 2) this._forceRelay = true;
      this.connectionUnstable = false;
      this.connected = false;
      this.roomJoined = false;
      this.webrtcConnected = false;
      this.hostConnected = false;
      try {
        if (this.webrtc) this.webrtc.disconnect();
      } catch (error) {
        console.log("⚠️ retry: webrtc teardown", error);
      }
      try {
        if (this.socket) this.socket.disconnect();
      } catch (error) {
        console.log("⚠️ retry: socket teardown", error);
      }
      this.socket = null;
      this.webrtc = null;
      this.initializeSocket();
      this.startConnectWatchdog();
    },

    // Initialize WebRTC for direct P2P communication
    async initializeWebRTC() {
      console.log("🚀 Initializing WebRTC as Guest");

      try {
        // A socket reconnect re-runs this method; tear down the previous service
        // first so its socket listeners are removed (no duplicate handlers stacking).
        if (this.webrtc) {
          try { this.webrtc.disconnect(); } catch (e) { /* already gone */ }
        }
        this.webrtc = new WebRTCService();
        // After repeated failures, force relay (TURN) — a direct/STUN path that
        // keeps failing behind mobile/symmetric NAT is skipped in favor of the
        // relay now that real TURN exists (P4-16 §C).
        if (this._forceRelay) {
          console.log("🔁 Retry: forcing relay (TURN) transport policy");
          this.webrtc.setIceTransportPolicy("relay");
        }
        // Set up WebRTC event handlers
        console.log("adding Tracks");
        this.webrtc.onCanvasUpdate = (canvasData) => {
          console.log("📥 Received canvas update via WebRTC");
          if (this.$refs.canvasStage) {
            this.$refs.canvasStage.applyCanvasUpdate(canvasData);
          }
        };

        // Handle remote track addition (WebRTC ontrack)
        this.webrtc.onTrack = (stream, track) => {
          console.log("📥 Remote track added via WebRTC", track);
          this.remoteStream = stream;
        };

        this.webrtc.onPermissionUpdate = (permissions) => {
          console.log("📥 Received permission update via WebRTC:", permissions);
          this.updatePermissions(permissions);
        };

        this.webrtc.onZoomUpdate = (zoomData) => {
          console.log("📥 Received zoom update via WebRTC:", zoomData);
          if (this.$refs.canvasStage) {
            this.$refs.canvasStage.applyZoomUpdate(zoomData);
          }
        };

        this.webrtc.onConnectionStateChange = (state) => {
          console.log("🔗 WebRTC connection state:", state);
          this.webrtcConnected = state === "connected";
          this.hostConnected = state === "connected";

          if (state === "connected") {
            console.log(
              "✅ WebRTC connection established automatically - no manual setup needed"
            );
            // P4-16 — fully connected: clear the unstable warning + watchdog.
            this.connectionUnstable = false;
            this.disarmReloadPrompt();
            this.clearConnectWatchdog();
            this.webrtc.sendMicToggle(!this.micMuted);
            // Dismiss loading screen
            setTimeout(() => {
              this.guestReady = true;
            }, 400);
          }
          if (state === "disconnected" || state === "failed") {
            console.log("❌ WebRTC connection lost/failed:", state);
            this.webrtcConnected = false;
            this.hostConnected = false;
            // Surface "reconnecting…" while the service auto-recovers (grace timer
            // then ICE-restart / renegotiation). Cleared when we return to
            // 'connected'. The manual retry button remains available as a fallback.
            this.connectionUnstable = true;
            this.armReloadPrompt();
          }
        };

        this.webrtc.onMessage = (message) => {
          console.log("📥 Received message via WebRTC:", message);

          // Handle session termination with enhanced debugging
          if (typeof message === "object" && message.type === "session-terminated") {
            console.log("🔴 Session termination message received via WebRTC:", message);
            this.handleSessionClosed(message.redirectUrl);
            return;
          }

          // Handle different message types
          if (typeof message === "object" && message.type === "initial_message") {
            // Add initial message to chat
            this.messages.push(message.message);
          } else {
            // Regular chat message
            const content = typeof message === "string" ? message : message.content;
            this.messages.push({
              id: Date.now(),
              from: "host",
              content: content,
              timestamp: new Date(),
            });
            this.showNewMessageToast(content, this.hostName || "Oda Sahibi");
          }

          // Auto-scroll to bottom
          this.scrollChatToBottom();
        };

        this.webrtc.onRemoteStreamAdded = (stream) => {
          console.log("📥 Remote stream added via WebRTC");
          this.remoteStream = stream;
          this.remoteVideoKey += 1; // Force re-render of GuestCamera component
        };

        // Handle when answer is automatically sent via socket
        this.webrtc.onAnswerReady = (answerData) => {
          console.log("📋 WebRTC answer automatically sent via socket");
          // No need to show manual connection data - everything is automatic now
        };

        // Fetch time-limited TURN/STUN credentials before init (P0-1).
        // P4-16 — retry on transient failures (slow/lossy 3G) before falling back
        // to STUN-only, which cannot traverse mobile/symmetric NAT. If every attempt
        // fails, flag the connection as unstable so the UI can explain the problem.
        const iceServers = await this.fetchTurnCredentials();
        if (iceServers) {
          this.webrtc.setIceServers(iceServers);
        } else {
          console.warn("Could not fetch TURN credentials, using default STUN");
          this.connectionUnstable = true;
        }

        // Initialize WebRTC as guest
        const success = await this.webrtc.initializeAsGuest(this.roomId, this.socket);
        if (success) {
          console.log("✅ WebRTC Guest initialized successfully");
        }

        await this.webrtc.addTracks(this.localStream);
        this.webrtc.setupSocketSignaling();

        this.webrtc.remoteVideoState = (state) => {
          console.log("📹 Remote video state changed:", state);
          this.remoteStream?.getVideoTracks().forEach((track) => {
            track.enabled = state;
          });
        };

        this.webrtc.remoteAudioState = (state) => {
          console.log("🎤 Remote mic state changed:", state);
          // this.remoteStream?.getAudioTracks().forEach((track) => {
          //   track.enabled = state;
          // });
          this.remoteMuted = !state;
        };
      } catch (error) {
        console.error("❌ Failed to initialize WebRTC:", error);
      }
    },

    joinRoom() {
      if (this.socket && this.roomId) {
        // Join room as guest
        this.socket.emit("peer-info", this.roomId, "peer", null);
        // P1-5 — join the Socket.IO room and request the current whiteboard
        // snapshot (server replies with `canvas-state`) for late-join.
        this.socket.emit("join-room", this.roomId);
        console.log("Guest joined room:", this.roomId);
        console.log("Socket connected:", this.socket.connected);
        this.roomJoined = true;

        // Assume host is connected since we successfully joined the room
        this.hostConnected = true;
        console.log("Setting hostConnected to true - ready to send canvas updates");

        // Initialize WebRTC automatically when joining room
        // WebRTC connection will be established automatically via socket signaling
        this.initializeWebRTC();
      } else {
        console.error("Cannot join room - missing socket or roomId:", {
          socket: !!this.socket,
          roomId: this.roomId,
        });
      }
    },

    sendMessage() {
      const messageContent = this.newMessage.trim();
      if (messageContent && this.socket && this.roomId) {
        // P1-6 — send chat over Socket.IO instead of the WebRTC data channel.
        // The server echoes it back to everyone (incl. us), so we render on
        // receipt in the `chat-msg` handler — do NOT push locally here or the
        // message would appear twice.
        this.socket.emit("chat-msg", this.roomId, "peer", messageContent);
        this.newMessage = "";
      }
    },

    handleHostJoined() {
      this.messages.push({
        id: Date.now(),
        from: "system",
        content: "Oda sahibi katıldı",
        timestamp: new Date(),
      });
    },

    handleSessionClosed(redirectUrl = null) {
      console.log("🔴 Guest handleSessionClosed called, redirectUrl:", redirectUrl);

      this.messages.push({
        id: Date.now(),
        from: "system",
        content: "Oturum Oda sahibi tarafından kapatıldı",
        timestamp: new Date(),
      });

      // Show brief message
      alert("Oturum sona erdi.");

      // Clean up connections first
      console.log("🧹 Cleaning up connections...");
      try {
        if (this.webrtc) {
          this.webrtc.disconnect();
        }
        if (this.socket) {
          this.socket.disconnect();
        }
        this.cleanupStreams();
      } catch (error) {
        console.log("⚠️ Error during cleanup:", error);
      }

      // Wait 2 seconds then redirect or go to online page
      setTimeout(() => {
        console.log("🔄 Attempting redirect/close...");

        if (redirectUrl && redirectUrl !== "null" && redirectUrl.trim() !== "") {
          try {
            console.log("🌐 Redirecting to:", redirectUrl);
            window.location.href = redirectUrl;
          } catch (error) {
            console.log("❌ Redirect failed:", error);
            console.log("� Fallback: going to /online");
            this.$router.push("/online");
          }
        } else {
          console.log("📱 No valid redirect URL, going to /online");
          this.$router.push("/online");
        }
      }, 2000);
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.chatMessages) {
            this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
          }
        }, 50);
      });
    },

    showNewMessageToast(content, sender) {
      this.$bvToast.toast(content, {
        title: `${sender} kişisinden yeni mesaj`,
        variant: 'info',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        appendToast: true
      });
    },

    // Existing methods
    handleToolChange(tool) {
      // Only allow tool change if guest has permission
      if (this.isToolAllowed(tool)) {
        this.activeTool = tool;
      }
    },

    handleUndo() {
      // Guests cannot undo (this should never be called due to UI restrictions)
      console.log("Guests cannot perform undo operations");
    },

    handleRedo() {
      // Guests cannot redo (this should never be called due to UI restrictions)
      console.log("Guests cannot perform redo operations");
    },

    isToolAllowed(tool) {
      switch (tool) {
        case "pen":
          return this.guestPermissions.canDraw;
        case "undo":
        case "redo":
          return false; // Guests never have undo/redo permissions
        case "rectangle":
        case "circle":
          return this.guestPermissions.canAddShapes;
        case "select":
          return this.guestPermissions.canSelect; // Guests cannot use select tool
        default:
          return false;
      }
    },

    handleToolOptionsChange(tool, options) {
      this.toolOptions[tool] = { ...this.toolOptions[tool], ...options };
    },

    handleCanvasUpdate(data) {
      // P1-5 — whiteboard sync now rides Socket.IO. Lightweight ops (strokes,
      // shapes, clear) go over the reliable server relay; the heavy base64
      // background still uses the WebRTC data channel until P1-7 replaces it
      // with a server URL (a `data:`/chunk payload marks the heavy path).
      const bg = data.backgroundImage;
      const heavy =
        (typeof bg === "string" && bg.startsWith("data:")) || (bg && bg.chunk);
      if (heavy) {
        if (this.webrtc && this.webrtcConnected && this.hostConnected) {
          this.webrtc.sendCanvasUpdate(data);
        }
      } else if (this.socket && this.roomId) {
        this.socket.emit("canvas-op", this.roomId, "peer", data);
      }
    },

    handleClearCanvas() {
      if (this.guestPermissions.canClear && this.$refs.canvasStage) {
        this.$refs.canvasStage.clearCanvas();
      }
    },

    toggleSidebars() {
      this.sidebarVisible = !this.sidebarVisible;
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      // Hide all sidebars when in fullscreen mode
      if (this.isFullscreen) {
        this.sidebarVisible = false;
        this.leftSidebarOpen = false;
        this.rightSidebarOpen = false;
        this.localCameraVisible = false;
        this.hostCameraVisible = false;
      } else {
        // Restore normal view
        this.sidebarVisible = true;
        this.localCameraVisible = true;
        this.hostCameraVisible = true;
      }
    },

    toggleLeftSidebar() {
      this.leftSidebarOpen = !this.leftSidebarOpen;
      // Close right sidebar if it's open to prevent overlap
      if (this.leftSidebarOpen && this.rightSidebarOpen) {
        this.rightSidebarOpen = false;
      }
    },

    toggleRightSidebar() {
      this.rightSidebarOpen = !this.rightSidebarOpen;
      // Close left sidebar if it's open to prevent overlap
      if (this.rightSidebarOpen && this.leftSidebarOpen) {
        this.leftSidebarOpen = false;
      }
    },

    closeMobileSidebars() {
      this.leftSidebarOpen = false;
      this.rightSidebarOpen = false;
    },

    handleKeydown(event) {
      // Close mobile sidebars on Escape key
      if (event.key === "Escape") {
        this.closeMobileSidebars();
      }
    },

    handleResize() {
      // Force canvas to re-render on window resize
      this.$nextTick(() => {
        if (this.$refs.canvasStage) {
          this.$refs.canvasStage.recalculateStage();
        }
      });
    },

    // Handle receiving permissions from host
    updatePermissions(newPermissions) {
      console.log("Updating guest permissions:", newPermissions);

      // Permanently block certain permissions for guests
      const guestRestrictedPermissions = {
        ...newPermissions,
        canClear: false, // Guests can never clear canvas
        canSelect: false, // Guests can never use select tool
        canUploadBackground: false, // Guests can never upload backgrounds
        canUndo: false, // Guests can never undo
        canRedo: false, // Guests can never redo
      };

      this.guestPermissions = { ...guestRestrictedPermissions };

      // If current tool is not allowed anymore, switch to an allowed tool
      if (!this.isToolAllowed(this.activeTool)) {
        if (this.guestPermissions.canDraw) {
          this.activeTool = "pen";
        } else if (this.guestPermissions.canAddShapes) {
          this.activeTool = "rectangle";
        } else {
          // If no tools are allowed, guest can only view
          this.activeTool = "pen"; // Keep pen but it won't work
        }
      }
    },
  },
  async mounted() {
    // Add window resize listener for responsive canvas
    window.addEventListener("resize", this.handleResize);
    // Add keydown listener for closing mobile sidebars
    window.addEventListener("keydown", this.handleKeydown);

    // Check devices on mount
    await this.checkAvailableDevices();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("keydown", this.handleKeydown);

    // P4-16 — cancel the connection watchdog timer.
    this.clearConnectWatchdog();
    this.disarmReloadPrompt();

    // Disconnect WebRTC
    if (this.webrtc) {
      this.webrtc.disconnect();
    }

    // Disconnect socket
    if (this.socket) {
      this.socket.disconnect();
    }

    // Clean up media streams
    this.cleanupStreams();
  },
};
</script>

<style src="./OnlineViewStyle.css" scoped></style>
<style scoped>
/* Guest-specific overrides */

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #2a1e25 0%, #1e1e1e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-card {
  text-align: center;
  padding: 48px 40px;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
}

.spinner-ring {
  position: absolute;
  inset: 0;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #de39a5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 28px;
  color: #de39a5;
}

/* P4-16 — access-denied + unstable-connection states */
.access-denied-icon {
  color: #f0a020;
  font-size: 40px;
}

.loading-unstable {
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(240, 160, 32, 0.12);
  border: 1px solid rgba(240, 160, 32, 0.3);
}

.unstable-text {
  color: #f0a020;
  font-size: 13px;
  font-weight: 500;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.retry-btn {
  background: #de39a5;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.retry-btn:hover {
  background: #c62f92;
}

.loading-title {
  color: #ffffff;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: 0.5px;
}

.loading-subtitle {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin: 0 0 28px;
}

.loading-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.loading-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  transition: all 0.4s ease;
}

.loading-step i {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.3);
  min-width: 18px;
  text-align: center;
  transition: color 0.4s ease;
}

.loading-step span {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  font-weight: 500;
  transition: color 0.4s ease;
}

.loading-step.done {
  background: rgba(40, 199, 111, 0.1); /* #28c76f with opacity */
}

.loading-step.done i {
  color: #28c76f;
}

.loading-step.done span {
  color: rgba(255, 255, 255, 0.85);
}

/* Fade transition for loading overlay */
.fade-loading-enter-active,
.fade-loading-leave-active {
  transition: opacity 0.5s ease;
}

.fade-loading-enter,
.fade-loading-leave-to {
  opacity: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.guest-status {
  background-color: #f8f9fa;
}

.status-info {
  text-align: center;
}

.status-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.connection-info {
  margin-bottom: 16px;
}

.connection-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
}

.connection-status.online {
  color: #28a745;
}

.connection-status i {
  font-size: 8px;
}

.permissions-summary h5 {
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: #555;
}

.permission-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.permission-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
  font-weight: 500;
}

.permission-item i {
  font-size: 10px;
  width: 12px;
}

.camera-toggle-btn.host {
  background-color: rgba(220, 53, 69, 0.9);
}

.camera-toggle-btn.host:hover {
  background-color: rgba(220, 53, 69, 1);
}

.maximize {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Mobile optimizations for guest status */
@media (max-width: 768px) {
  .status-title {
    font-size: 13px;
    margin-bottom: 8px;
  }

  .connection-status {
    font-size: 11px;
  }

  .permissions-summary h5 {
    font-size: 11px;
    margin-bottom: 6px;
  }

  .permission-item {
    font-size: 10px;
    padding: 3px 6px;
    gap: 4px;
  }

  .permission-item i {
    font-size: 9px;
    width: 10px;
  }
}

/* Chat styles */
.chat-container {
  display: flex;
  flex-direction: column;
  height: 300px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #e1e5e9;
  background-color: #f8f9fa;
}

.chat-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #dc3545;
}

.connection-status.connected {
  color: #28a745;
}

.connection-status i {
  font-size: 8px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  background-color: #fff;
}

.message {
  margin-bottom: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  max-width: 80%;
}

.message.own {
  background-color: #28a745;
  color: white;
  margin-left: auto;
  text-align: right;
}

.message:not(.own):not(.system) {
  background-color: #e9ecef;
  color: #333;
}

.message.system {
  background-color: #fff3cd;
  color: #856404;
  text-align: center;
  font-style: italic;
  font-size: 12px;
  margin-left: auto;
  margin-right: auto;
}

.message-content {
  font-size: 13px;
  line-height: 1.4;
}

.message-time {
  font-size: 10px;
  opacity: 0.7;
  margin-top: 2px;
}

.chat-input {
  display: flex;
  padding: 8px;
  border-top: 1px solid #e1e5e9;
  background-color: #f8f9fa;
}

.message-input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  width: 100%;
}

.message-input:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.25);
}

.send-btn {
  margin-left: 8px;
  padding: 6px 12px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.send-btn:hover {
  background-color: #218838;
}

/* Automatic WebRTC Connection Status */
.connection-status .connection-container {
  background-color: #f0f8ff;
  border: 1px solid #b3d7ff;
  border-radius: 8px;
  padding: 12px;
}

.connection-status .connection-header {
  margin-bottom: 8px;
}

.connection-status .connection-header h5 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #0066cc;
}

.connection-status .connection-info {
  text-align: center;
}

.connection-status .connection-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #dc3545;
}

.connection-status .connection-status.connected {
  color: #28a745;
}

.connection-status .connection-status i {
  font-size: 8px;
}
.permission-status {
  margin-bottom: 10px;
  font-size: 12px;
}

.device-status {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.audio-status,
.video-status {
  display: flex;
  align-items: center;
  gap: 5px;
}

.no-devices {
  color: #dc3545;
  text-align: center;
  padding: 10px;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}
</style>
